import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { TextLinkBack } from '@nebula/text-link';
import { BenefitsList, StyledTitleLarge500 } from '../CoolingOffPeriodStage/index.styles';
import { CancelButton, StyledTitleSmall500, ButtonGroup, KeepButton, Container, InfoContainer } from './index.styles';
import {
  ImmediateCancelHappensNext,
  DeferredCancelHappensNext,
  getSubscriptionName,
  formatDateToReadable,
  CANCELLATION_TYPES,
} from '../../constants';
import Loader from './loader';
import ConfirmCancelDialog from '../../../ConfirmCancelDialog';
import { trackCoolingOffCancelClick, trackCoolingOffConfirmCancelClick } from '../../../../helpers/switch/analytics';
import downsellSubscription from '../../../../actions/fulfillments/downsell';
import clearSession from '../../../../helpers/clearSession';
import updateSession from '../../../../actions/session/update';
import syncSession from '../../../../actions/session/sync';
import { push } from 'connected-react-router';
import usePageView from '../../../../hooks/usePageView';
import cancelSubscriptionTransition, { CancelSubscriptionStep } from '../../../../helpers/cancelSubscriptionTransition';

export default function ConfirmCancelStage({
  onBack,
  onKeepSubscription,
  onDeferredCancel,
  onImmediateCancel,
  isWebview,
  isFlexFlow,
  subscriptionFamily,
  nextBillingDate,
  isDeferred,
  isImmediate,
  goToResult,
  goToErrorPage,
}) {
  const dispatch = useDispatch();
  const session = useSelector(state => state.session);
  const subscriptionName = getSubscriptionName(subscriptionFamily);
  const formattedDate = formatDateToReadable(nextBillingDate);

  const PAGE_NAME = 'ProdMoveCoolingOffConfirmCancelPage';

  const happensNextList = isDeferred ? DeferredCancelHappensNext(formattedDate) : ImmediateCancelHappensNext;

  const [isDialogShowing, setIsDialogShowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onNotNow = () => {
    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.BACK });
      return;
    }

    dispatch(push('/'));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  usePageView(PAGE_NAME);

  const dialogBodyText = isDeferred
    ? `You’ll still have access to ${subscriptionName} until ${formattedDate}.`
    : `Access to ${subscriptionName} will end today.`;

  const handleKeepSubscription = () => {
    onKeepSubscription('confirmation');
  };

  const handleCancelSubscription = () => {
    trackCoolingOffCancelClick(
      subscriptionFamily,
      isDeferred ? CANCELLATION_TYPES.DEFERRED : CANCELLATION_TYPES.IMMEDIATE,
      'proceed'
    );
    setIsDialogShowing(true);
  };

  const handleConfirmCancellation = async () => {
    setIsLoading(true);

    try {
      if (isDeferred && onDeferredCancel) {
        trackCoolingOffConfirmCancelClick(subscriptionFamily, CANCELLATION_TYPES.DEFERRED);
        await onDeferredCancel(() => {
          // On success, move to result stage instead of thanks page
          goToResult();
        });
      } else if (isImmediate) {
        trackCoolingOffConfirmCancelClick(subscriptionFamily, CANCELLATION_TYPES.IMMEDIATE);
        if (isFlexFlow && onImmediateCancel) {
          await onImmediateCancel(() => goToResult());
        } else {
          const result = await dispatch(downsellSubscription());

          // Check if the action indicates an error or if the operation failed
          if (result.error || !result.payload?.success) {
            throw new Error('Downsell failed');
          }
          await clearSession(
            session,
            newSession => dispatch(updateSession(newSession)),
            () => dispatch(syncSession())
          );

          goToResult();
        }
      }
    } catch (error) {
      goToErrorPage();
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loader isWebview={isWebview} subscriptionName={subscriptionName} />;
  }

  return (
    <Container isWebview={isWebview}>
      <InfoContainer>
        {!isWebview && <TextLinkBack onClick={onBack}>Back</TextLinkBack>}
        <StyledTitleLarge500>
          {`Cancel ${subscriptionName} ${isDeferred ? 'on ' + formattedDate : 'now'}`}
        </StyledTitleLarge500>
        <StyledTitleSmall500>What happens next?</StyledTitleSmall500>
        <BenefitsList>
          {happensNextList.map(item => (
            <li key={item}>{item}</li>
          ))}
        </BenefitsList>
      </InfoContainer>
      <ButtonGroup>
        <CancelButton label={`Cancel ${subscriptionName}`} onClick={handleCancelSubscription} type="button" />
        <KeepButton label={`Keep ${subscriptionName}`} onClick={handleKeepSubscription} type="button" />
      </ButtonGroup>

      <ConfirmCancelDialog
        isShowing={isDialogShowing}
        toggleHide={() => setIsDialogShowing(false)}
        onConfirm={handleConfirmCancellation}
        bodyText={dialogBodyText}
        subscriptionName={subscriptionName}
        onNotNow={onNotNow}
        subscriptionFamily={subscriptionFamily}
      />
    </Container>
  );
}

ConfirmCancelStage.propTypes = {
  onBack: PropTypes.func.isRequired,
  onKeepSubscription: PropTypes.func.isRequired,
  onDeferredCancel: PropTypes.func,
  onImmediateCancel: PropTypes.func,
  isWebview: PropTypes.bool,
  isFlexFlow: PropTypes.bool,
  subscriptionFamily: PropTypes.string.isRequired,
  nextBillingDate: PropTypes.string,
  isDeferred: PropTypes.bool.isRequired,
  isImmediate: PropTypes.bool.isRequired,
  goToResult: PropTypes.func.isRequired,
  goToErrorPage: PropTypes.func.isRequired,
};

ConfirmCancelStage.defaultProps = {
  onDeferredCancel: null,
  onImmediateCancel: null,
  isWebview: false,
  isFlexFlow: false,
  nextBillingDate: null,
};
